import React from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { fade, makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ActionButton from '../../../components/ActionButton';
import useImportHaccpAgent from './useImportHaccpAgent';
import conteggio from './import_haccp/conteggio';

// The tile behind an icon: the colour at low opacity.
const tinta = (colore) => fade(colore, 0.12);

const useStyles = makeStyles((theme) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  icona: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: theme.spacing(5),
    height: theme.spacing(5),
    borderRadius: theme.shape.borderRadius,
  },
  iconaDaImportare: {
    backgroundColor: tinta(theme.palette.primary.main),
    color: theme.palette.primary.main,
  },
  // `dark` rather than `main`: the green `main` sits under 3:1 on its own tint.
  iconaImportata: {
    backgroundColor: tinta(theme.palette.success.main),
    color: theme.palette.success.dark,
  },
  // Neutral on purpose: only the check failed, the configuration did not.
  iconaIncerta: {
    backgroundColor: theme.palette.neutral.light,
    color: theme.palette.text.secondary,
  },
  testo: {
    flexGrow: 1,
    textAlign: 'left',
  },
  titolo: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  bottone: {
    flexShrink: 0,
  },
  segnaposto: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.placeholder.main,
  },
  barra: {
    height: theme.spacing(1.5),
  },
  barraTitolo: {
    width: '35%',
    marginBottom: theme.spacing(1),
  },
  barraSottotitolo: {
    width: '55%',
  },
  segnapostoBottone: {
    flexShrink: 0,
    width: theme.spacing(22),
    height: theme.spacing(5),
  },
}));

// The date and the time are formatted apart and joined by hand: a single
// `toLocaleString` reads «18 settembre 2026 alle ore 11:24» on some ICU versions.
const formattaDataImport = (dataImport) => {
  const data = new Date(dataImport);
  const giorno = data.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const ora = data.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${giorno}, ${ora}`;
};

// «18 settembre 2026, 11:24 — importate 12 procedure, 34 schede». The counts
// are what the import brought, not what the GeOp holds after later edits, hence
// «importate». The participle agrees with the first count: «importata 1
// procedura». The counts are left out unless both arrived: half of them would
// read as a GeOp with no schede.
const sottotitoloImportata = ({ dataImport, procedure, schede }) => {
  const data = formattaDataImport(dataImport);
  if (procedure == null || schede == null) {
    return data;
  }
  const participio = procedure === 1 ? 'importata' : 'importate';
  return `${data} — ${participio} ${conteggio(procedure, 'procedura', 'procedure')}, ${conteggio(schede, 'scheda', 'schede')}`;
};

// What a settled read shows, per state. `primario` gives the prominent button:
// a second import replaces the configuration without keeping the old one, so
// only a GeOp with nothing imported gets it. The error state keeps the
// first-import wording but not the prominence, since a configuration may exist.
const PRIMO_IMPORT = {
  titolo: 'Configurazione da HACCP Agent',
  etichetta: 'Importa Export JSON',
};

const CONTENUTO_PER_STATO = {
  assente: () => ({
    ...PRIMO_IMPORT,
    Icona: GetAppIcon,
    classeIcona: 'iconaDaImportare',
    sottotitolo:
      "Nessuna configurazione importata da HACCP Agent. Se la Gestione Operativa è già stata configurata, l'import la sostituisce.",
    primario: true,
  }),
  errore: () => ({
    ...PRIMO_IMPORT,
    Icona: ErrorOutlineIcon,
    classeIcona: 'iconaIncerta',
    sottotitolo:
      'Non è stato possibile verificare se è già stata importata una configurazione.',
    primario: false,
  }),
  presente: (importazione) => ({
    Icona: CheckCircleIcon,
    classeIcona: 'iconaImportata',
    // The workspace is missing when the imported file's header did not carry it.
    titolo: importazione.workspace
      ? `Configurazione importata dal workspace ${importazione.workspace}`
      : 'Configurazione importata da HACCP Agent',
    sottotitolo: sottotitoloImportata(importazione),
    etichetta: 'Sostituisci con un nuovo file',
    primario: false,
  }),
};

/**
 * The provenance of a Gestione Operativa: whether a configuration from HACCP
 * Agent has been imported, and from which workspace and when.
 *
 * The import itself is not started here: the button calls `onImporta`. The
 * banner reads the import again on every new `ricaricaToken`, which whoever
 * runs an import bumps once it succeeds. Without
 * `aggiornamentoGestioneOperativaId` there is nothing to read yet, and the
 * banner stays on its placeholder.
 *
 * A failed read keeps the button: importing does not depend on this check, so
 * the banner says the check failed and leaves the decision to the operator.
 */
const BannerConfigurazioneHaccp = ({
  aggiornamentoGestioneOperativaId,
  onImporta,
  ricaricaToken,
}) => {
  const classes = useStyles();
  const lettura = useImportHaccpAgent(
    aggiornamentoGestioneOperativaId,
    ricaricaToken,
  );

  if (lettura.stato === 'caricamento') {
    return (
      <div
        className={classes.banner}
        role="status"
        aria-label="Verifica della configurazione importata in corso"
      >
        <div className={`${classes.icona} ${classes.segnaposto}`} />
        <div className={classes.testo}>
          <div
            className={`${classes.segnaposto} ${classes.barra} ${classes.barraTitolo}`}
          />
          <div
            className={`${classes.segnaposto} ${classes.barra} ${classes.barraSottotitolo}`}
          />
        </div>
        <div className={`${classes.segnaposto} ${classes.segnapostoBottone}`} />
      </div>
    );
  }

  const { Icona, classeIcona, titolo, sottotitolo, etichetta, primario } =
    CONTENUTO_PER_STATO[lettura.stato](lettura.importazione);
  const onClick = () => onImporta && onImporta();

  return (
    <div className={classes.banner}>
      <div className={`${classes.icona} ${classes[classeIcona]}`}>
        <Icona aria-hidden="true" />
      </div>
      <div className={classes.testo}>
        <Typography
          variant="subtitle1"
          component="h2"
          className={classes.titolo}
        >
          {titolo}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {sottotitolo}
        </Typography>
      </div>
      {primario ? (
        <div className={classes.bottone}>
          <ActionButton label={etichetta} onClick={onClick} disableMargin />
        </div>
      ) : (
        <Button
          variant="outlined"
          className={classes.bottone}
          onClick={onClick}
        >
          {etichetta}
        </Button>
      )}
    </div>
  );
};

BannerConfigurazioneHaccp.propTypes = {
  aggiornamentoGestioneOperativaId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onImporta: PropTypes.func,
  ricaricaToken: PropTypes.number,
};

export default BannerConfigurazioneHaccp;
